import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Section from '../components/Section';
import {BottomNavigator} from '../components/Navbar';
import useArticles, {fetchArticle} from '../hooks/useArticles';
import useSpeech from '../hooks/useSpeech';
import useReveal from '../hooks/useReveal';
import ScrollReveal from '../components/ScrollReveal';
import BackToTop from '../components/BackToTop';
import ArticleCover from '../components/ArticleCover';

/* ---- Icons ---- */
const Svg = ({children, filled, ...rest}) => (
    <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke={filled ? 'none' : 'currentColor'}
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>{children}</svg>
);
const IconPlay = () => <Svg filled><path d="M8 5.14v13.72c0 .82.9 1.31 1.58.86l10.29-6.86a1.03 1.03 0 0 0 0-1.72L9.58 4.28A1.03 1.03 0 0 0 8 5.14Z"/></Svg>;
const IconPause = () => <Svg filled><rect x="6" y="4.5" width="4.2" height="15" rx="1.4"/><rect x="13.8" y="4.5" width="4.2" height="15" rx="1.4"/></Svg>;
const IconStop = () => <Svg filled><rect x="6" y="6" width="12" height="12" rx="2"/></Svg>;
const IconSpeaker = () => <Svg><path d="M4 9v6h4l5 4V5L8 9H4Z"/><path d="M16.5 8.5a5 5 0 0 1 0 7"/></Svg>;
const IconSearch = () => <Svg><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></Svg>;
const IconBack = () => <Svg><path d="m15 18-6-6 6-6"/></Svg>;
const IconClock = () => <Svg><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></Svg>;

const splitSentences = (text) => {
    const parts = text.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g);
    return (parts || [text]).map((s) => s.trim()).filter(Boolean);
};

// The article promoted to the "Read latest" hero slot on the Library page.
const FEATURED_ARTICLE_ID = 'personalized-cancer-therapies__the-ghost-in-the-machine-why-2026-is-the-year-mrna-becomes-bio-software';

// Round-robin articles across topics so adjacent cards are different topics
// (a diverse-looking grid) instead of listing every article of a topic together.
const interleaveByTopic = (articles) => {
    const groups = new Map();
    for (const a of articles) {
        if (!groups.has(a.category)) groups.set(a.category, []);
        groups.get(a.category).push(a);
    }
    const buckets = [...groups.values()];
    const out = [];
    for (let i = 0; ; i++) {
        let added = false;
        for (const b of buckets) {
            if (b[i]) { out.push(b[i]); added = true; }
        }
        if (!added) break;
    }
    return out;
};

/* Podcast photo with a generative-SVG fallback if the image is missing/fails. */
const Cover = memo(({article}) => {
    const [failed, setFailed] = useState(false);
    if (article.image && !failed) {
        return <img className="ArtCoverImg" src={article.image} alt="" loading="lazy" onError={() => setFailed(true)} />;
    }
    return <ArticleCover topic={article.category} seed={article.id} />;
});

/* ================= Article card ================= */
const ArticleCard = memo(({article, onOpen, index = 0}) => {
    const [ref, shown] = useReveal();
    return (
        <button ref={ref} type="button"
            className={`ArtCard Reveal${shown ? ' is-in' : ''}`}
            style={{'--rd': `${(index % 3) * 80}ms`}}
            onClick={() => onOpen(article.id)}>
            <span className="ArtCard__cover"><Cover article={article} /></span>
            <span className="ArtCard__body">
                <span className="ArtCard__cat">{article.category}</span>
                <span className="ArtCard__title">{article.title}</span>
                <span className="ArtCard__excerpt">{article.excerpt}</span>
                <span className="ArtCard__meta">
                    <span className="ArtCard__time"><IconClock /> {article.readingTime} min read</span>
                    <span className="ArtCard__listen"><IconSpeaker /> Listen</span>
                </span>
            </span>
        </button>
    );
});

const fmtTime = (t) => {
    if (!Number.isFinite(t) || t < 0) t = 0;
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
};
const AUDIO_SPEEDS = [1, 1.25, 1.5, 1.75, 2];

/* Plays a pre-generated narration MP3 with play/pause, seek, and speed. */
const AudioListen = ({src}) => {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [cur, setCur] = useState(0);
    const [dur, setDur] = useState(0);
    const [rate, setRate] = useState(1);

    const toggle = () => {
        const a = ref.current;
        if (!a) return;
        a.paused ? a.play().catch(() => {}) : a.pause();
    };
    const onSeek = (e) => {
        const v = Number(e.target.value);
        if (ref.current) ref.current.currentTime = v;
        setCur(v);
    };
    const changeRate = (r) => {
        setRate(r);
        if (ref.current) ref.current.playbackRate = r;
    };

    const pct = dur ? (cur / dur) * 100 : 0;
    return (
        <div className="Listen Listen--audio">
            <audio
                ref={ref}
                src={src}
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onTimeUpdate={() => setCur(ref.current ? ref.current.currentTime : 0)}
                onLoadedMetadata={() => { if (ref.current) { setDur(ref.current.duration); ref.current.playbackRate = rate; } }}
            />
            <button type="button" className={`Listen__play${playing ? ' is-playing' : ''}`} onClick={toggle}>
                {playing ? <IconPause /> : <IconPlay />}
                <span>{playing ? 'Pause' : 'Listen to this article'}</span>
            </button>
            <div className="Listen__scrub">
                <span className="Listen__time">{fmtTime(cur)}</span>
                <input
                    className="Listen__range" type="range" min="0" max={dur || 0} step="1"
                    value={Math.min(cur, dur || 0)} style={{'--pct': `${pct}%`}}
                    onChange={onSeek} aria-label="Seek"
                />
                <span className="Listen__time">{fmtTime(dur)}</span>
            </div>
            <div className="Listen__speed" role="group" aria-label="Playback speed">
                {AUDIO_SPEEDS.map((r) => (
                    <button key={r} type="button" className={rate === r ? 'is-active' : ''} onClick={() => changeRate(r)}>{r}×</button>
                ))}
            </div>
        </div>
    );
};

/* ================= Reader ================= */
const FONT_KEY = 'lavita-reader-font-scale';

const Reader = ({article, loading, error, onClose, onOpen, related, speech}) => {
    const bodyRef = useRef(null);
    const chunkMapRef = useRef([]); // [{text, block}]

    const [fontScale, setFontScale] = useState(() => {
        const v = Number(window.localStorage.getItem(FONT_KEY));
        return Number.isFinite(v) && v >= 0.8 && v <= 1.6 ? v : 1;
    });
    useEffect(() => {
        try { window.localStorage.setItem(FONT_KEY, String(fontScale)); } catch { /* storage may be unavailable */ }
    }, [fontScale]);

    // Build TTS chunks (sentence-level) + tag block elements for highlighting.
    useEffect(() => {
        speech.stop();
        chunkMapRef.current = [];
        if (!article || !bodyRef.current) return;
        const blocks = [...bodyRef.current.querySelectorAll('h2,h3,h4,p,li,blockquote')];
        const map = [{text: article.title, block: -1}];
        blocks.forEach((el, bi) => {
            el.setAttribute('data-block', String(bi));
            splitSentences(el.textContent || '').forEach((s) => map.push({text: s, block: bi}));
        });
        chunkMapRef.current = map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [article]);

    // Highlight + auto-scroll the paragraph currently being read.
    useEffect(() => {
        const root = bodyRef.current;
        if (!root) return;
        root.querySelectorAll('.tts-active').forEach((el) => el.classList.remove('tts-active'));
        const map = chunkMapRef.current;
        if (speech.current < 0 || !map[speech.current]) return;
        const bi = map[speech.current].block;
        if (bi < 0) return;
        const el = root.querySelector(`[data-block="${bi}"]`);
        if (el) {
            el.classList.add('tts-active');
            el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [speech.current]);

    const onListen = useCallback(() => {
        if (speech.speaking && !speech.paused) { speech.pause(); return; }
        if (speech.speaking && speech.paused) { speech.resume(); return; }
        speech.play(chunkMapRef.current.map((c) => c.text));
    }, [speech]);

    const listenLabel = !speech.speaking ? 'Listen' : speech.paused ? 'Resume' : 'Pause';

    return (
        <article className="Reader">
            <div className="Reader__bar">
                <button type="button" className="Reader__back" onClick={onClose}><IconBack /> Articles</button>
                <div className="Reader__fontsize" title="Text size">
                    <span className="Reader__fontsize-a Reader__fontsize-a--sm" aria-hidden="true">A</span>
                    <input
                        type="range" min="0.85" max="1.5" step="0.05" value={fontScale}
                        onChange={(e) => setFontScale(Number(e.target.value))}
                        aria-label="Article text size" className="Reader__fontsize-range"
                    />
                    <span className="Reader__fontsize-a Reader__fontsize-a--lg" aria-hidden="true">A</span>
                </div>
            </div>

            {loading && <p className="ArtStatus">Loading article…</p>}
            {error && <p className="ArtStatus ArtStatus--error">{error}</p>}

            {article && !loading && (
                <>
                    <div className="Reader__cover"><Cover article={article} /></div>
                    <header className="Reader__head">
                        <span className="Reader__cat">{article.category}</span>
                        <h1 className="Reader__title">{article.title}</h1>
                        <div className="Reader__meta">
                            <span><IconClock /> {article.readingTime} min read</span>
                        </div>

                        {article.audio ? (
                            <AudioListen key={article.id} src={article.audio} />
                        ) : speech.supported ? (
                            <div className="Listen">
                                <button type="button" className={`Listen__play${speech.speaking && !speech.paused ? ' is-playing' : ''}`} onClick={onListen}>
                                    {speech.speaking && !speech.paused ? <IconPause /> : <IconPlay />}
                                    <span>{listenLabel} to this article</span>
                                </button>
                                {speech.speaking && (
                                    <button type="button" className="Listen__stop" onClick={speech.stop} aria-label="Stop"><IconStop /></button>
                                )}
                                <div className="Listen__speed" role="group" aria-label="Reading speed">
                                    {[0.9, 1, 1.25, 1.5].map((r) => (
                                        <button key={r} type="button" className={speech.rate === r ? 'is-active' : ''} onClick={() => speech.setRate(r)}>{r}×</button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="Listen__unsupported">Listening isn’t supported in this browser.</p>
                        )}
                    </header>

                    <div className="Reader__body" ref={bodyRef} style={{'--reader-scale': fontScale}} dangerouslySetInnerHTML={{__html: article.html}} />

                    {related.length > 0 && (
                        <footer className="Reader__related">
                            <h2>More in {article.category}</h2>
                            <div className="Reader__related-list">
                                {related.map((r) => (
                                    <button key={r.id} type="button" className="Reader__related-item" onClick={() => onOpen(r.id)}>
                                        <span className="Reader__related-title">{r.title}</span>
                                        <span className="Reader__related-time">{r.readingTime} min read</span>
                                    </button>
                                ))}
                            </div>
                        </footer>
                    )}
                </>
            )}
        </article>
    );
};

/* ================= Page ================= */
const ArticlesPage = () => {
    const {categories, articles, loading, error, refresh} = useArticles();
    const speech = useSpeech();

    const [activeCat, setActiveCat] = useState('All');
    const [query, setQuery] = useState('');

    const [openId, setOpenId] = useState(null);
    const [article, setArticle] = useState(null);
    const [articleLoading, setArticleLoading] = useState(false);
    const [articleError, setArticleError] = useState(null);

    // Clip the off-canvas mobile menu's horizontal overflow while on this page.
    useEffect(() => {
        document.documentElement.classList.add('ArticlesNoXScroll');
        return () => document.documentElement.classList.remove('ArticlesNoXScroll');
    }, []);

    const ordered = useMemo(() => interleaveByTopic(articles), [articles]);

    const view = useMemo(() => {
        const q = query.trim().toLowerCase();
        return ordered.filter((a) => {
            const catOk = activeCat === 'All' || a.category === activeCat;
            const qOk = !q || a.title.toLowerCase().includes(q) || a.category.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q);
            return catOk && qOk;
        });
    }, [ordered, activeCat, query]);

    const openArticle = useCallback((id) => {
        setOpenId(id);
        if (window.location.hash !== `#read-${id}`) {
            window.history.replaceState(null, '', `#read-${id}`);
        }
        window.scrollTo(0, 0);
    }, []);

    const closeArticle = useCallback(() => {
        speech.stop();
        setOpenId(null);
        setArticle(null);
        window.history.replaceState(null, '', window.location.pathname);
        window.scrollTo(0, 0);
    }, [speech]);

    // Fetch the open article's content
    useEffect(() => {
        if (!openId) return undefined;
        const controller = new AbortController();
        setArticleLoading(true);
        setArticleError(null);
        setArticle(null);
        fetchArticle(openId, controller.signal)
            .then((data) => setArticle(data))
            .catch((e) => { if (e.name !== 'AbortError') setArticleError('This article could not be loaded.'); })
            .finally(() => { if (!controller.signal.aborted) setArticleLoading(false); });
        return () => controller.abort();
    }, [openId]);

    // Deep-link support (#read-<id>)
    useEffect(() => {
        const sync = () => {
            const h = window.location.hash;
            if (h.startsWith('#read-')) setOpenId(h.slice('#read-'.length));
        };
        sync();
        window.addEventListener('hashchange', sync);
        return () => window.removeEventListener('hashchange', sync);
    }, []);

    const related = useMemo(() => {
        if (!article) return [];
        return articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 4);
    }, [article, articles]);

    const featured = articles.find((a) => a.id === FEATURED_ARTICLE_ID) || articles[0];

    if (openId) {
        return (
            <div className="Page ArticlesPage ArticlesPage--reading">
                <Section className="ArticlesSection" id="article">
                    <Reader
                        article={article}
                        loading={articleLoading}
                        error={articleError}
                        onClose={closeArticle}
                        onOpen={openArticle}
                        related={related}
                        speech={speech}
                    />
                </Section>
                <BottomNavigator next={{to: '/podcast', title: 'Podcast'}} />
                <BackToTop />
            </div>
        );
    }

    return (
        <div className="Page ArticlesPage">
            <Section className="ArticlesSection" id="articles-top">
                {/* Hero */}
                <div className="ArtHero">
                    <div className="ArtHero__copy">
                        <ScrollReveal as="span" className="ArtHero__eyebrow" threshold={0.1} variant="left"><span className="ArtHero__dot" aria-hidden="true"/>The Lavita Library</ScrollReveal>
                        <ScrollReveal as="h1" className="ArtHero__title" threshold={0.1} delay={90} variant="up">Frontier medicine, <span className="ArtHero__accent">in snapshot.</span></ScrollReveal>
                        <ScrollReveal as="p" className="ArtHero__lede" threshold={0.1} delay={180} variant="up">
                            Deep-dive explainers on the science shaping the future of health — from AI diagnostics
                            and longevity medicine to the microbiome. Read them, or press play and listen.
                        </ScrollReveal>
                        <ScrollReveal as="div" className="ArtHero__cta" threshold={0.1} delay={260} variant="up">
                            <button type="button" className="ArtBtn ArtBtn--primary" onClick={() => featured && openArticle(featured.id)} disabled={!featured}>
                                Read latest
                            </button>
                            <a className="ArtBtn ArtBtn--ghost" href="#articles">Browse all articles</a>
                        </ScrollReveal>
                        <ScrollReveal as="div" className="ArtHero__stats" threshold={0.1} delay={330} variant="up">
                            <div className="ArtStat"><span className="ArtStat__n">{articles.length || '—'}</span><span className="ArtStat__l">Articles</span></div>
                            <div className="ArtStat"><span className="ArtStat__n">{categories.length || '—'}</span><span className="ArtStat__l">Topics</span></div>
                            <div className="ArtStat"><span className="ArtStat__n">100%</span><span className="ArtStat__l">Science-backed</span></div>
                        </ScrollReveal>
                    </div>
                    {featured && (
                        <ScrollReveal as="div" className="ArtHero__featuredWrap" threshold={0.1} delay={160} variant="right">
                            <button type="button" className="ArtHero__featured" onClick={() => openArticle(featured.id)} aria-label={`Read the latest article: ${featured.title}`}>
                                <span className="ArtHero__featured-cover"><Cover article={featured} /></span>
                                <span className="ArtHero__featured-overlay">
                                    <span className="ArtTag">Latest</span>
                                    <span className="ArtHero__featured-cat">{featured.category}</span>
                                    <span className="ArtHero__featured-title">{featured.title}</span>
                                    <span className="ArtHero__featured-meta"><IconClock /> {featured.readingTime} min read</span>
                                </span>
                            </button>
                        </ScrollReveal>
                    )}
                </div>

                {/* Library */}
                <div className="ArtLibrary" id="articles">
                    <div className="ArtLibrary__head">
                        <ScrollReveal as="h2" threshold={0.4} variant="up">Articles</ScrollReveal>
                        <div className="ArtSearch">
                            <span className="ArtSearch__icon" aria-hidden="true"><IconSearch /></span>
                            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                                   placeholder="Search articles or topics…" aria-label="Search articles" />
                        </div>
                    </div>

                    <div className="ArtFilters" id="topics" role="group" aria-label="Filter by topic">
                        {['All', ...categories].map((cat) => (
                            <button key={cat} type="button" className="ArtChip" aria-pressed={activeCat === cat} onClick={() => setActiveCat(cat)}>{cat}</button>
                        ))}
                    </div>

                    {loading && <p className="ArtStatus">Loading articles…</p>}
                    {error && (
                        <div className="ArtStatus ArtStatus--error">
                            <p>{error}</p>
                            <button type="button" onClick={refresh}>Try again</button>
                        </div>
                    )}
                    {!loading && !error && view.length === 0 && <p className="ArtStatus">No articles match your search.</p>}

                    <div className="ArtGrid">
                        {view.map((a, i) => <ArticleCard key={a.id} article={a} index={i} onOpen={openArticle} />)}
                    </div>
                </div>

                <ScrollReveal as="div" className="ArtAbout" id="about" threshold={0.3} variant="up">
                    <h2>About the Library</h2>
                    <p>
                        The Lavita Library distills frontier research from leading medical and healthcare journals into
                        clear, focused reads across {categories.length || 'many'} topics — from AI-driven diagnostics to
                        longevity medicine. Every article can be read or listened to, for the Lavita community.
                    </p>
                </ScrollReveal>
            </Section>

            <BottomNavigator next={{to: '/podcast', title: 'Podcast'}} />
            <BackToTop />
        </div>
    );
};

export default ArticlesPage;
